import { store, RootState } from '../store';
import { mergeStyleSets } from '@fluentui/react';
import { IButtonStyles } from '@fluentui/react';
import {
    resolveThemeColors as resolveThemeColorsShared,
    contrastTextColor,
    isBrightColor,
    darken,
    lighten,
    ResolvedThemeColors,
    Theme,
    NextcloudThemingCapabilities,
} from '@sendent/nextcloud-api';

export const BODY_FONT_STACK =
    'Nunito, Segoe UI, system-ui, -apple-system, sans-serif';
export const DISPLAY_FONT_STACK = `Avenir, Nunito Sans, ${BODY_FONT_STACK}`;

/**
 * Returns a six digit hex color as rgba with the given alpha, or transparent
 * for any other value.
 * @param hex
 * @param alpha
 */
export const tint = (hex: string, alpha: number): string => {
    const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex || '');
    if (!match) return 'transparent';
    const [r, g, b] = match.slice(1).map((part) => parseInt(part, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

let lastColorsTheme: Theme | null | undefined;
let lastColorsNextcloudTheming: NextcloudThemingCapabilities | null | undefined;
let cachedColors: ResolvedThemeColors | null = null;

// Cheap to call repeatedly: re-derives colors only when theme/nextcloudTheming
// actually changed (by reference — Redux/immer keep untouched state branches
// referentially stable). Every appStyles.* getter and every
// useSelector(resolveThemeColors, ...) call site benefits from this.
export function resolveThemeColors(state: RootState): ResolvedThemeColors {
    const theme = state?.authentication?.theme;
    const nextcloudTheming = state?.authentication?.nextcloudTheming;

    if (
        cachedColors &&
        theme === lastColorsTheme &&
        nextcloudTheming === lastColorsNextcloudTheming
    ) {
        return cachedColors;
    }

    lastColorsTheme = theme;
    lastColorsNextcloudTheming = nextcloudTheming;
    cachedColors = resolveThemeColorsShared(theme, nextcloudTheming);
    return cachedColors;
}

const getSettingsTheme = (colors: ResolvedThemeColors) => {
    return {
        settingsPanel: {
            borderTop: 'solid 1px #c8c6c4',
            cursor: 'pointer',
            paddingTop: 6,
        },
        settingsText: {
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: 'normal',
            color: colors.appBgText,
        },
        settingsIconContainer: {
            width: '20px',
            height: '16px',
            borderLeft: 'solid 2px #c8c6c4',
            paddingLeft: 8,
        },
        settingsIcon: {
            color: colors.icon,
            fontSize: 12,
        },
        settingsHeaderLayout: {
            backgroundColor: 'transparent',
            paddingBottom: 5,
            paddingTop: 5,
            paddingLeft: 5,
            paddingRight: 5,
            marginTop: 20,
            marginBottom: 15,
        },
        settingsTitle: {
            fontSize: 21,
            fontFamily: DISPLAY_FONT_STACK,
            fontStyle: 'normal',
            fontWeight: '600',
            color: colors.headerFont,
        },
        settingsSectionTitle: {
            fontSize: 16,
            fontFamily: DISPLAY_FONT_STACK,
            fontStyle: 'normal',
            fontWeight: '600',
            color: colors.appBgText,
        },
        settingsBack: {
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: 'normal',
            color: colors.icon,
        },
        settingsLicenseLabel: {
            fontSize: 10,
            fontStyle: 'normal',
            fontWeight: 'normal',
            color: colors.appBgText,
        },
        settingsLicenseText: {
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: 'normal',
            color: colors.appBgText,
        },
    };
};

const getTabsTheme = (colors: ResolvedThemeColors) => {
    return {
        tabs: {
            display: 'flex',
            backgroundColor: colors.appBg,
            padding: '0px',
            margin: '0px',
            borderBottom: '1px solid #f8f8f8',
            selectors: {
                ['& + .license-level-guard-warning-container']: {
                    marginTop: '0.5rem',
                },
            },
        },
        tabsItem: {
            marginBottom: '0px',
            padding: '10px 10px 0px 10px',
            cursor: 'pointer',
            flex: '1',
            color: colors.appBgText,
            fontSize: '12px',
        },
        tabsItemActive: {
            marginBottom: '0px',
            padding: '10px 10px 0px 10px',
            cursor: 'pointer',
            flex: '1',
            color: colors.appBgText,
            fontSize: '12px',
            border: 'solid ' + colors.icon,
            borderWidth: '0px 0px 2px 0px',
        },
        tabsLabel: {
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: 'normal',
            color: colors.appBgText,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
        },
    };
};

const buildGlobalStyles = (colors: ResolvedThemeColors) => {
    return mergeStyleSets(
        {
            pageHeaderLayout: {
                backgroundColor: 'transparent',
                paddingBottom: 5,
                paddingTop: 5,
                paddingLeft: 5,
                paddingRight: 5,
                marginTop: 20,
                marginBottom: 15,
                textAlign: 'center',
            },
            pageHeader: {
                fontSize: 21,
                fontFamily: DISPLAY_FONT_STACK,
                color: colors.headerFont,
                fontStyle: 'normal',
                fontWeight: '600',
            },
            text: {
                fontSize: 14,
                fontStyle: 'normal',
                fontWeight: 'normal',
            },
            label: {
                fontSize: 14,
                fontStyle: 'normal',
                fontWeight: '600',
                textAlign: 'left',
                color: colors.appBgText,
            },
            part: {
                fontSize: 18,
                fontFamily: DISPLAY_FONT_STACK,
                fontStyle: 'normal',
                fontWeight: '600',
            },
            comment: {
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 'normal',
            },
            file: {
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 'normal',
            },
            date: {
                fontSize: 10,
                fontStyle: 'normal',
                fontWeight: 'normal',
            },
            errorLoginStack: {
                paddingLeft: 12,
                paddingTop: 12,
                paddingRight: 12,
                paddingBottom: 12,
                boxSizing: 'border-box',
                wordBreak: 'break-word',
                color: colors.primaryFont,
                background: colors.primary,
            },
            sectionLayout: {
                paddingTop: 20,
            },
            sectionLayout10: {
                paddingTop: 10,
            },
            sectionLayout20: {
                height: '52px',
                paddingTop: 20,
            },
            sectionLayout30: {
                paddingTop: 30,
            },
            sectionLayout50: {
                paddingTop: 50,
            },
            sectionLayoutBorder: {
                marginTop: '30px !important',
                paddingTop: '15px',
                borderTop: '1px solid #98908c',
            },
            filesContent: {
                display: 'inline-block',
                height: '320px',
                width: '100%',
                border: '1px solid #98908c',
                padding: 5,
                overflow: 'scroll',
                zIndex: 100,
            },
            optionIconNotSet: {
                fontSize: 20,
                color: colors.appBgText,
            },
            optionIconSet: {
                fontSize: 20,
                color: 'green',
            },
            wizardContent: {
                minHeight: '110px',
            },
            scrollWizard: {
                marginTop: '63px !important',
                marginBottom: '70px !important',
            },
            scrollWizardNoConfirm: {
                marginTop: '63px !important',
                marginBottom: '0px !important',
            },
            wizardButtons: {
                position: 'absolute',
                bottom: '4px',
                right: '0px',
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
                paddingTop: '0px',
                borderTop: '1px solid #98908c',
            },
            error: {
                textTransform: 'none',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '14px',
                textAlign: 'left',
                color: 'white',
                borderRadius: '4px',
                background: '#D01C23',
            },
        },
        getSettingsTheme(colors),
        getTabsTheme(colors),
    );
};

let lastGlobalStylesColors: ResolvedThemeColors | null = null;
let cachedGlobalStyles: ReturnType<typeof buildGlobalStyles> | null = null;

// mergeStyleSets is the expensive part of buildGlobalStyles — cache its
// result too, keyed off the (already-memoized) colors reference.
const globalStyles = () => {
    const colors = resolveThemeColors(store.getState());

    if (cachedGlobalStyles && colors === lastGlobalStylesColors) {
        return cachedGlobalStyles;
    }

    lastGlobalStylesColors = colors;
    cachedGlobalStyles = buildGlobalStyles(colors);
    return cachedGlobalStyles;
};

const getTextBoxStyle = () => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            label: {
                fontStyle: 'normal',
                fontWeight: '600',
                fontSize: '14px',
                textAlign: 'left',
                color: colors.appBgText,
            },
        },
        fieldGroup: {
            borderColor: colors.appBgText,
            borderRadius: '2px',
        },
        field: {
            selectors: {
                '::placeholder': {
                    color: colors.appBgText,
                    opacity: 0.6,
                },
            },
        },
    };
};

const getDropDownStyle = () => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            label: {
                fontStyle: 'normal',
                fontWeight: '600',
                fontSize: '14px',
                textAlign: 'left',
                color: colors.appBgText,
            },
        },
    };
};

const getChoiceStyle = () => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            label: {
                fontSize: 14,
                fontStyle: 'normal',
                fontWeight: 'normal',
                textAlign: 'left',
                color: colors.appBgText,
            },
        },
    };
};

const getCheckBoxStyle = () => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            selectors: {
                ':hover .ms-Checkbox-label .ms-Checkbox-checkbox': {
                    backgroundColor: colors.primaryHover,
                    borderColor: colors.primaryHover,
                },
                ':hover .ms-Checkbox-label .ms-Checkbox-checkmark': {
                    backgroundColor: colors.primaryHover,
                    opacity: '1',
                },
            },
        },
        text: {
            fontSize: 14,
            color: '#858585',
            fontStyle: 'normal',
            fontWeight: 'normal',
        },
        checkmark: {
            color: colors.primaryFont,
        },
        checkbox: {
            backgroundColor: colors.primary,
            borderColor: colors.primary,
        },
    };
};

const getToggleStyle = () => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            selectors: {
                ':hover .ms-Toggle-background': {
                    backgroundColor: colors.primaryHover,
                },
            },
        },
        pill: {
            backgroundColor: colors.primary,
        },
    };
};

export const getFileRowHoverColor = (colors: ResolvedThemeColors): string => {
    return isBrightColor(colors.appBg)
        ? darken(colors.appBg, 12)
        : lighten(colors.appBg, 15);
};

const primaryButton = (): IButtonStyles => {
    const colors = resolveThemeColors(store.getState());
    const hoverColor = contrastTextColor(colors.primaryHover);
    return {
        root: {
            border: '0px',
            backgroundColor: colors.primary + ' !important',
            color: colors.primaryFont + ' !important',
            borderRadius: '2px',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '600 !important',
        },
        rootHovered: {
            backgroundColor: colors.primaryHover + ' !important',
            color: hoverColor + ' !important',
            border: '0px',
        },
        rootExpanded: {
            backgroundColor: colors.primary + ' !important',
            border: '0px',
        },
        rootFocused: {
            backgroundColor: colors.primary + ' !important',
            border: '0px',
        },
    };
};

const primaryButtonReverse = (): IButtonStyles => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            border: '1px solid ' + colors.primary + ' !important',
            backgroundColor: 'transparent !important',
            color: colors.appBgText + ' !important',
            borderRadius: '2px',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '600 !important',
        },
        rootHovered: {
            backgroundColor: 'transparent !important',
            border: '0px',
        },
        rootExpanded: {
            border: '1px solid ' + colors.primary + ' !important',
            backgroundColor: 'transparent !important',
        },
        rootFocused: {
            border: '1px solid ' + colors.primary + ' !important',
            backgroundColor: 'transparent !important',
        },
    };
};

const actionButton = (): IButtonStyles => {
    return {
        root: {
            textDecoration: 'underline',
            height: '16px',
        },
    };
};

const actionButtonEdit = (): IButtonStyles => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            color: colors.icon + ' !important',
            fontSize: '14px',
            height: '16px',
        },
    };
};

const actionButtonRemove = (): IButtonStyles => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            color: colors.appBgText + ' !important',
            fontSize: '12px',
            height: '14px',
        },
        icon: {
            color: colors.icon,
            fontSize: '12px',
        },
    };
};

const actionButtonFiles = (): IButtonStyles => {
    const colors = resolveThemeColors(store.getState());
    return {
        root: {
            color: colors.appBgText + ' !important',
            fontSize: '12px',
            height: '16px',
        },
        rootHovered: {
            color: colors.primary + ' !important',
        },
        icon: {
            color: colors.primary,
            fontSize: '12px',
        },
        flexContainer: {
            flexDirection: 'row-reverse',
        },
    };
};

const menuIconButton = (): IButtonStyles => {
    const colors = resolveThemeColors(store.getState());
    return {
        icon: { color: colors.icon },
        iconHovered: { color: colors.appBgText },
    };
};

const folderIconButton = (): IButtonStyles => {
    const colors = resolveThemeColors(store.getState());
    return {
        icon: { color: colors.icon },
    };
};

const DialogStyle = () => {
    const colors = resolveThemeColors(store.getState());
    return {
        main: {
            background: colors.popupBg,
            color: colors.appBgText,
            width: '380px !important',
            maxWidth: '380px !important',
        },
        title: {
            color: colors.appBgText,
        },
        subText: {
            color: colors.appBgText,
        },
    };
};

export const appStyles = {
    get globalStyles() {
        return globalStyles();
    },
    get primaryButton() {
        return primaryButton();
    },
    get primaryButtonReverse() {
        return primaryButtonReverse();
    },
    get actionButton() {
        return actionButton();
    },
    get actionButtonEdit() {
        return actionButtonEdit();
    },
    get actionButtonRemove() {
        return actionButtonRemove();
    },
    get actionButtonFiles() {
        return actionButtonFiles();
    },
    get menuIconButton() {
        return menuIconButton();
    },
    get folderIconButton() {
        return folderIconButton();
    },
    get DialogStyle() {
        return DialogStyle();
    },
    get checkBoxStyle() {
        return getCheckBoxStyle();
    },
    get textBoxStyle() {
        return getTextBoxStyle();
    },
    get dropDownStyle() {
        return getDropDownStyle();
    },
    get choiceStyle() {
        return getChoiceStyle();
    },
    get toggleStyle() {
        return getToggleStyle();
    },
};
